import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ExternalLink, Plus, ZoomIn } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Reveal, Overline } from "./Reveal";
import { GALLERY, FAQS, mapsLink } from "../../lib/data";

export const Showcase = () => {
  const [lb, setLb] = useState({ open: false, index: 0 });
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* Reviews */}
      <section className="py-24 md:py-32 bg-[#F5F7FA]">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="rounded-[2.5rem] bg-white border border-gray-100 shadow-xl shadow-[#071A33]/5 p-10 md:p-16 text-center">
            <Reveal>
              <Overline>Dipercaya Pelanggan</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="font-heading font-extrabold text-6xl md:text-7xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] bg-clip-text text-transparent">
                  5.0
                </span>
                <span className="flex flex-col items-start">
                  <span className="flex text-[#FBBF24]">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-current" />)}
                  </span>
                  <span className="text-sm text-[#6B7280] mt-1">dari 3 Google Reviews</span>
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-lg mx-auto text-[#6B7280] leading-relaxed">
                Rating sempurna dari pelanggan yang telah mempercayakan kebutuhan
                teknologi dan keamanan mereka kepada SATCOM MEDIA TEKNOLOGI.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <a
                href="https://www.google.com/search?q=SATCOM+MEDIA+TEKNOLOGI+Rangkasbitung"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-gray-200 px-6 py-3 text-sm font-semibold text-[#111827] hover:bg-[#F5F7FA] transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Lihat Google Reviews
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="galeri" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <Reveal><Overline>Galeri</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-bold text-[#111827] text-4xl md:text-5xl tracking-tight">
                Portofolio & Aktivitas
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-sm text-[#6B7280]">
                Dokumentasi nyata dari lapangan — hasil pemasangan CCTV, parabola, dan sistem kasir oleh tim SATCOM MEDIA TEKNOLOGI.
              </p>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {GALLERY.map((g, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06} className={i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""}>
                <button
                  onClick={() => setLb({ open: true, index: i })}
                  data-testid={`gallery-item-${i}`}
                  className={`group relative w-full overflow-hidden rounded-2xl border border-gray-100 ${i === 0 ? "h-full min-h-[240px] md:min-h-[420px]" : "aspect-[4/3]"}`}
                >
                  <img src={g.src} alt={g.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#071A33]/80 via-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                  <span className="absolute left-4 bottom-4 text-white font-semibold text-sm">{g.label}</span>
                  <span className="absolute right-4 top-4 grid place-items-center h-9 w-9 rounded-full bg-white/20 backdrop-blur text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="h-4 w-4" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
        <Lightbox
          open={lb.open}
          close={() => setLb({ ...lb, open: false })}
          index={lb.index}
          slides={GALLERY.map((g) => ({ src: g.src, description: g.label }))}
        />
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-[#F5F7FA]">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <Reveal><Overline>FAQ</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-bold text-[#111827] text-4xl md:text-5xl tracking-tight">
                Pertanyaan yang Sering Diajukan
              </h2>
            </Reveal>
          </div>

          <div className="mt-12">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={i} delay={i * 0.04}>
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      data-testid={`faq-item-${i}`}
                      className="w-full flex items-center justify-between gap-4 py-5 text-left"
                    >
                      <span className="font-heading font-semibold text-[#111827] text-lg">{f.q}</span>
                      <span className={`grid place-items-center h-8 w-8 rounded-full bg-white border border-gray-200 text-[#0B63F6] shrink-0 transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
                        <Plus className="h-4 w-4" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <p className="pb-5 text-[#6B7280] leading-relaxed">{f.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
